import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Drawer,
  Chip,
} from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import SuperAdminLayout from '../../components/layout/SuperAdmin/SuperAdminLayout';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import DealerForm from './components/DealerForm';
import {
  dealerEndpoints,
  Dealer,
  CreateDealerDTO,
} from '../../services/api/endpoints/dealer.endpoint';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';

const DealersView = () => {
  const theme = useTheme();

  // Data state
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UI state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Confirmation Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    cognitoId: '',
  });

  // Notification state
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const showNotification = (message: string, severity: SEVERITY) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const fetchDealers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dealerEndpoints.getDealers();
      setDealers(data);
    } catch (error) {
      console.error('Failed to fetch dealers:', error);
      // Don't show error for empty results
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  const handleCreateDealer = async (data: CreateDealerDTO) => {
    setIsSubmitting(true);
    try {
      await dealerEndpoints.createDealer(data);
      showNotification(
        'Dealer created successfully. An email with temporary password has been sent.',
        SEVERITY.SUCCESS
      );
      setIsDrawerOpen(false);
      await fetchDealers();
    } catch (error: any) {
      const message = error?.message || 'Failed to create dealer';
      showNotification(message, SEVERITY.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (cognitoId: string) => {
    setConfirmDialog({
      open: true,
      cognitoId,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await dealerEndpoints.deleteDealer(confirmDialog.cognitoId);
      showNotification('Dealer deleted successfully', SEVERITY.SUCCESS);
      await fetchDealers();
    } catch (error) {
      showNotification('Failed to delete dealer', SEVERITY.ERROR);
    } finally {
      setConfirmDialog({ open: false, cognitoId: '' });
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Dealers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage dealer accounts and their promo codes
            </Typography>
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={20} />}
            onClick={() => setIsDrawerOpen(true)}
          >
            Create Dealer
          </Button>
        </Box>

        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : !dealers.length ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
            >
              <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No dealers found" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                No dealers found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first dealer to get started
              </Typography>
              <Button
                variant="contained"
                disableElevation
                startIcon={<Plus size={20} />}
                onClick={() => setIsDrawerOpen(true)}
                sx={{ mt: 3 }}
              >
                Create Dealer
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Email</TableCell>
                    <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>
                      Promo Code
                    </TableCell>
                    <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Phone</TableCell>
                    <TableCell
                      align="right"
                      sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealers.map(dealer => (
                    <TableRow
                      key={dealer.cognitoId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ px: 3, fontSize: 16 }}>
                        {dealer.firstName
                          ? `${dealer.firstName} ${dealer.lastName || ''}`.trim()
                          : 'Not Provided'}
                      </TableCell>
                      <TableCell>{dealer.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={dealer.promoCode || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{dealer.mobileNo || 'Not Provided'}</TableCell>
                      <TableCell align="right" sx={{ px: 3 }}>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(dealer.cognitoId)}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Create Dealer Drawer */}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => !isSubmitting && setIsDrawerOpen(false)}
        >
          <DealerForm
            onSubmit={handleCreateDealer}
            onClose={() => setIsDrawerOpen(false)}
            isLoading={isSubmitting}
          />
        </Drawer>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={confirmDialog.open}
          title="Delete Dealer"
          message="Are you sure you want to delete this dealer? This action cannot be undone. Organizations linked to this dealer's promo code will no longer have dealer management."
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDialog({ open: false, cognitoId: '' })}
        />

        {/* Notifications */}
        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Box>
    </SuperAdminLayout>
  );
};

export default DealersView;
